import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getAppointments, updateAppointment } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { StatCards } from "../components/StatCards.tsx";
import { AppointmentCard } from "../components/AppointmentCard.tsx";
import { WeekBar } from "../components/WeekBar.tsx";
import { PlusIcon } from "../components/Icon.tsx";
import { CheckoutModal } from "../components/CheckoutModal.tsx";

interface Appointment {
  id: string;
  start_at: string;
  end_at: string;
  status: "confirmed" | "tentative" | "completed" | "no_show" | "cancelled";
  customer_name: string;
  customer_phone: string;
  service_name: string;
  staff_name: string;
  notes?: string | null;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function getFirstName(email: string | undefined): string {
  if (!email) return "there";
  const local = email.split("@")[0];
  const first = local.split(/[._]/)[0];
  return first.charAt(0).toUpperCase() + first.slice(1);
}

function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function emphasis(count: number): string {
  if (count === 0) return "Nothing on the books";
  if (count === 1) return "One visit";
  return `${count} visits`;
}

function leadCopy(date: Date, today: Date, appts: Appointment[]): string {
  const sameDay = date.toDateString() === today.toDateString();
  const upcoming = appts.filter((a) => a.status === "confirmed");

  if (upcoming.length === 0) {
    if (!sameDay) return "Nothing scheduled. Hold the day open or add a booking.";
    return "A clear day. Catch up on admin, or schedule a walk-in below.";
  }

  const next = upcoming[0];
  const nextTime = new Date(next.start_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const allSameStaff = upcoming.every((a) => a.staff_name === next.staff_name);
  const staffPhrase = allSameStaff && next.staff_name ? `with ${next.staff_name}` : "";

  if (upcoming.length === 1) {
    return `${next.customer_name} at ${nextTime}${staffPhrase ? ", " + staffPhrase : ""}.`;
  }
  return `Starts at ${nextTime}${staffPhrase ? ", " + staffPhrase : ""}.`;
}

function dateKicker(date: Date): { dow: string; date: string } {
  const dow = date.toLocaleDateString(undefined, { weekday: "long" });
  const dateStr = date.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  return { dow, date: dateStr };
}

export default function Today() {
  const { slug } = useParams<{ slug: string }>();
  const { session } = useAuth();

  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);

  const dateStr = toDateStr(selectedDate);

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    getAppointments(slug, { date: dateStr })
      .then((data) => {
        const sorted = [...(data as Appointment[])].sort(
          (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
        );
        setAppointments(sorted);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug, dateStr]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener("zedule:refresh", handler);
    return () => window.removeEventListener("zedule:refresh", handler);
  }, [load]);

  const onAction = useCallback(
    async (id: string, status: string) => {
      if (!slug) return;
      setActionLoading(id);
      try {
        await updateAppointment(slug, id, { status });
        setAppointments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, status: status as Appointment["status"] } : a))
        );
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setActionLoading(null);
      }
    },
    [slug]
  );

  const upcoming = appointments.filter((a) => a.status === "confirmed").length;
  const completed = appointments.filter((a) => a.status === "completed").length;
  const noShow = appointments.filter((a) => a.status === "no_show").length;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const firstName = getFirstName(session?.user?.email);
  const greeting = getGreeting();
  const { dow, date } = dateKicker(selectedDate);
  const emphasisText = emphasis(upcoming);
  const lead = leadCopy(selectedDate, today, appointments);

  // Next-in time for the primary KPI sub-line.
  const nextAppt = appointments.find((a) => a.status === "confirmed" && new Date(a.start_at) > new Date());
  const upcomingSub = nextAppt ? formatNextIn(nextAppt.start_at) : undefined;

  const openNew = () => window.dispatchEvent(new Event("zedule:new-appointment"));

  return (
    <div>
      {/* Hero */}
      <section className="zd-hero">
        <div className="zd-hero-text">
          <div className="zd-hero-kicker">
            <span className="live-dot" /> {dow} <span className="dot">·</span> {date}
          </div>
          <h1 className="zd-hero-headline">
            {greeting}, {firstName}. <em>{emphasisText}</em>
            {upcoming === 0 ? "." : " on the books."}
          </h1>
          <p className="zd-hero-lead">{lead}</p>
        </div>
        <div className="zd-hero-actions">
          <button
            type="button"
            className="zd-hero-cta"
            onClick={() => setShowCheckout(true)}
            aria-label="Collect payment"
            title="Collect payment"
            style={{
              background: "transparent",
              color: "var(--ink)",
              border: "1px solid var(--rule)",
              boxShadow: "none",
            }}
          >
            Collect payment
          </button>
          <button
            type="button"
            className="zd-hero-cta"
            onClick={openNew}
            aria-label="New appointment"
            title="New appointment"
          >
            <PlusIcon /> New appointment
          </button>
        </div>
      </section>

      {/* Day strip */}
      <WeekBar selectedDate={selectedDate} onSelect={setSelectedDate} />

      {/* KPIs */}
      <StatCards
        upcoming={upcoming}
        completed={completed}
        noShow={noShow}
        upcomingSub={upcomingSub}
      />

      {/* Schedule section */}
      <div className="zd-section">
        <h2>Schedule</h2>
        <div className="right">
          {appointments.length > 0 && (
            <>{appointments.length} {appointments.length === 1 ? "appointment" : "appointments"}</>
          )}
        </div>
      </div>

      {error && (
        <div className="alert-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: "48px 0", textAlign: "center", color: "var(--ink-faint)", fontSize: 14 }}>
          Loading…
        </div>
      ) : appointments.length === 0 ? (
        <div className="zd-empty">
          <h3>Nothing scheduled.</h3>
          <p>Add a walk-in or a booking for this day. The schedule lives here once it fills up.</p>
          <div className="cta-row">
            <button className="zd-hero-cta" onClick={openNew}>
              <PlusIcon /> New appointment
            </button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {appointments.map((appt) => (
            <AppointmentCard
              key={appt.id}
              appointment={appt}
              onAction={onAction}
              loading={actionLoading === appt.id}
            />
          ))}
        </div>
      )}
      {showCheckout && slug && (
        <CheckoutModal
          slug={slug}
          standalone
          onClose={() => setShowCheckout(false)}
          onDone={() => { load(); }}
        />
      )}
    </div>
  );
}

function formatNextIn(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return "Now";
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `Next in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes - hours * 60;
  if (hours < 24) {
    return remMin === 0 ? `Next in ${hours} hr` : `Next in ${hours} hr ${remMin} min`;
  }
  const days = Math.round(hours / 24);
  return `Next in ${days} day${days === 1 ? "" : "s"}`;
}
